'use client';

import React, { type FocusEvent, useCallback, useRef, useState } from 'react';

import cn from 'classnames';

import FormControl from '../FormControl/FormControl';
import type { InputProps } from './Input.types';

import styles from './styles/Input.module.css';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      value,
      size = 'medium',
      type = 'text',
      hint,
      pattern,
      error,
      label,
      fullWidth = false,
      disabled,
      readOnly,
      rightIcon,
      rightAddons,
      rightAddonsClassName,
      leftIcon,
      leftIconClassName,
      leftLabelIcon,
      labelPlacement,
      onFocus,
      onBlur,
      onChange,
      onInput,
      className,
      wrapperClassName,
      dataTestId,
      id,
      ...restProps
    },
    ref,
  ) => {
    const inputClassNames = cn('typography-subheading-4', styles.input, className, {
      [styles.hasLeftIcon]: leftIcon,
      [styles.hasRightIcon]: rightIcon || rightAddons,
      [styles.hasLeftLabelIcon]: leftLabelIcon,
    });
    const ariaLabel = typeof label === 'string' ? label : restProps['aria-label'];
    const inputValue = typeof value === 'object' ? value.value : value;
    const internalRef = useRef<HTMLInputElement>(null);
    const masterRef = ref ?? internalRef;
    const [isFocused, setIsFocused] = useState(restProps.autoFocus);
    const isFilled = !!value || !!internalRef?.current?.value || !!restProps.defaultValue;
    const handleInputFocus = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        if (!readOnly) setIsFocused(true);
        if (onFocus) onFocus(event);
      },
      [onFocus, readOnly],
    );
    const handleInputBlur = useCallback(
      (event: FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        if (onBlur) onBlur(event);
      },
      [onBlur],
    );

    return (
      <FormControl
        className={cn(wrapperClassName, { [styles.small]: size === 'small' })}
        disabled={disabled}
        error={error}
        filled={isFilled || isFocused}
        dateFilled={type === 'date' && value === ''}
        focused={isFocused}
        fullWidth={fullWidth}
        hint={hint}
        label={label}
        labelPlacement={labelPlacement}
        leftIcon={leftIcon}
        leftIconClassName={leftIconClassName}
        rightAddons={rightAddons}
        rightAddonsClassName={rightAddonsClassName}
        rightIcon={rightIcon}
        id={id}
      >
        <div className={styles.inputFieldWrapper}>
          {leftLabelIcon && <span className={styles.leftLabelIcon}>{leftLabelIcon}</span>}
          <input
            {...restProps}
            id={id}
            aria-label={ariaLabel}
            className={inputClassNames}
            data-test-id={dataTestId}
            disabled={disabled}
            onBlur={handleInputBlur}
            onChange={onChange}
            onFocus={handleInputFocus}
            onInput={onInput}
            pattern={pattern}
            readOnly={readOnly}
            type={type}
            value={inputValue}
            ref={masterRef}
          />
        </div>
      </FormControl>
    );
  },
);

Input.displayName = 'Input';
